"use client"

import { useEffect, useState, useCallback } from "react"
import { useSearchParams } from "next/navigation"
import { createClient } from "@/lib/supabase/client"
import { useAuth } from "@/lib/auth/context"
import { Card, CardContent } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Calendar, MapPin, Users, ChevronRight, Search, UserPlus, MessageCircle, Check, X, Clock, ChevronDown, UserCheck, Waves, Star } from "lucide-react"
import Link from "next/link"
import { format, startOfDay } from "date-fns"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { triggerHaptic } from "@/hooks/use-platform"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { ScrollArea } from "@/components/ui/scroll-area"

interface Member {
  id: string
  auth_id: string
  name: string
  first_name: string | null
  last_name: string | null
  avatar_url: string | null
  location: string | null
  surf_experience: string | null
  slug: string | null
}

interface FriendRequest {
  id: string
  user_id: string
  friend_id: string
  status: string
  created_at: string
  sender: Member
}

interface PlannedSession {
  id: string
  planned_date: string
  planned_time: string | null
  notes: string | null
  surf_spots: {
    id: string
    name: string
    country: string | null
  }
  users: {
    id: string
    auth_id: string
    name: string | null
    first_name: string | null
    last_name: string | null
    email: string
    avatar_url: string | null
  }
}

interface FriendParty {
  id: string
  title: string
  party_date: string
  start_time: string | null
  activity_type: "surf" | "kite"
  surf_spots: {
    name: string
  }
  organizer: {
    name: string | null
    first_name: string | null
    last_name: string | null
    avatar_url: string | null
  }
}

interface SurfSession {
  id: string
  session_date: string
  user_experience_rating: number | null
  conditions_rating: number | null
  notes: string | null
  surf_spots: {
    id: string
    name: string
    country: string | null
  }
  users: {
    id: string
    auth_id: string
    name: string | null
    first_name: string | null
    last_name: string | null
    email: string
    avatar_url: string | null
  }
}

export function FriendsExplore() {
  const { user, loading: authLoading } = useAuth()
  const [members, setMembers] = useState<Member[]>([])
  const [friends, setFriends] = useState<Member[]>([])
  const [pendingRequests, setPendingRequests] = useState<FriendRequest[]>([])
  const [sentRequests, setSentRequests] = useState<string[]>([])
  const [sessions, setSessions] = useState<PlannedSession[]>([])
  const [friendParties, setFriendParties] = useState<FriendParty[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [showFriendsList, setShowFriendsList] = useState(false)
  const [showDiscoverList, setShowDiscoverList] = useState(false)
  const [showSessionsHistory, setShowSessionsHistory] = useState(false)
  const [showPartiesHistory, setShowPartiesHistory] = useState(false)
  const [historySessions, setHistorySessions] = useState<SurfSession[]>([])
  const [historyParties, setHistoryParties] = useState<FriendParty[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [isActionLoading, setIsActionLoading] = useState<string | null>(null)
  
  const searchParams = useSearchParams()

  useEffect(() => {
    const showHistory = searchParams.get("showHistory")
    if (showHistory === "sessions") {
      setShowSessionsHistory(true)
    } else if (showHistory === "parties") {
      setShowPartiesHistory(true)
    }
  }, [searchParams])

  const supabase = createClient()

  const fetchHistory = useCallback(async () => {
    if (!user || !supabase || friends.length === 0) return
    setLoadingHistory(true)
    try {
      const internalFriendIds = friends.map(f => f.id)
      const friendAuthIds = friends.map(f => f.auth_id)
      const today = format(startOfDay(new Date()), "yyyy-MM-dd")

      // Fetch Session History
      const { data: sessionHistory, error: sessionError } = await supabase
        .from("surf_sessions")
        .select(`
          id, session_date, user_experience_rating, conditions_rating, notes,
          surf_spots (id, name, country),
          users!surf_sessions_user_id_fkey (id, auth_id, name, first_name, last_name, email, avatar_url)
        `)
        .in("user_id", friendAuthIds)
        .order("session_date", { ascending: false })
        .limit(30)
      
      if (sessionError) console.error("[FriendsExplore] Session history error:", sessionError)
      setHistorySessions(sessionHistory as any || [])

      // Fetch Party History
      const { data: partyHistory } = await supabase
        .from("surf_parties")
        .select(`
          id, title, party_date, start_time, activity_type,
          surf_spots (name),
          organizer:users!surf_parties_organizer_id_fkey(name, first_name, last_name, avatar_url)
        `)
        .in("organizer_id", internalFriendIds)
        .lt("party_date", today)
        .order("party_date", { ascending: false })
        .limit(30)
      
      setHistoryParties(partyHistory as any || [])
    } catch (e) {
      console.error("[FriendsExplore] Error fetching history:", e)
    } finally {
      setLoadingHistory(false)
    }
  }, [user, supabase, friends])

  useEffect(() => {
    if (showSessionsHistory || showPartiesHistory) {
      fetchHistory()
    }
  }, [showSessionsHistory, showPartiesHistory, fetchHistory])

  const fetchData = useCallback(async () => {
    if (!user || !supabase) return
    setLoading(true)

    try {
      // 1. Fetch All Members (browsing)
      const { data: allMembers } = await supabase
        .from("users")
        .select("id, auth_id, name, first_name, last_name, avatar_url, location, surf_experience, slug")
        .eq("is_active", true)
        .neq("auth_id", user.id)
        .limit(50)

      setMembers(allMembers || [])

      // 2. Fetch Friendships
      const { data: friendships } = await supabase
        .from("friends")
        .select("user_id, friend_id, status, id, created_at")
        .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`)

      if (friendships) {
        const acceptedFriendIds = friendships
          .filter(f => f.status === "accepted")
          .map(f => f.user_id === user.id ? f.friend_id : f.user_id)
        
        const pendingReceived = friendships.filter(f => f.status === "pending" && f.friend_id === user.id)
        const pendingSent = friendships.filter(f => f.status === "pending" && f.user_id === user.id)
        setSentRequests(pendingSent.map(f => f.friend_id))

        // Fetch Friend Details
        if (acceptedFriendIds.length > 0) {
          const { data: friendDetails } = await supabase
            .from("users")
            .select("id, auth_id, name, first_name, last_name, avatar_url, location, surf_experience, slug")
            .in("auth_id", acceptedFriendIds)
          setFriends(friendDetails || [])

          // Fetch Friends' Plans
          const today = format(startOfDay(new Date()), "yyyy-MM-dd")
          const internalFriendIds = (friendDetails || []).map(f => f.id)
          
          if (internalFriendIds.length > 0) {
            const { data: plannedSessions } = await supabase
              .from("planned_surf_sessions")
              .select(`
                id, planned_date, planned_time, notes,
                surf_spots (id, name, country),
                users!planned_surf_sessions_user_id_fkey(id, auth_id, name, first_name, last_name, email, avatar_url)
              `)
              .in("user_id", internalFriendIds)
              .gte("planned_date", today)
              .order("planned_date", { ascending: true })
            setSessions(plannedSessions as any || [])

            // Fetch Friends' Parties
            const { data: friendPartyData } = await supabase
              .from("surf_parties")
              .select(`
                id, title, party_date, start_time, activity_type,
                surf_spots (name),
                organizer:users!surf_parties_organizer_id_fkey(name, avatar_url)
              `)
              .in("organizer_id", internalFriendIds)
              .gte("party_date", today)
              .order("party_date", { ascending: true })
            setFriendParties(friendPartyData as any || [])
          }
        } else {
          setFriends([])
          setSessions([])
          setFriendParties([])
        }

        // Fetch Pending Requester Details
        if (pendingReceived.length > 0) {
          const requesterIds = pendingReceived.map(f => f.user_id)
          const { data: requesterDetails } = await supabase
            .from("users")
            .select("id, auth_id, name, first_name, last_name, avatar_url, location, surf_experience, slug")
            .in("auth_id", requesterIds)
          
          const requestsWithDetails = pendingReceived.map(req => ({
            ...req,
            sender: (requesterDetails || []).find(u => u.auth_id === req.user_id)!
          })).filter(r => r.sender)
          setPendingRequests(requestsWithDetails as any)
        } else {
          setPendingRequests([])
        }
      }
    } catch (error) {
      console.error("[FriendsExplore] Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }, [user, supabase])

  const filteredFriends = friends.filter(f => 
    f.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.location?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredMembers = members.filter(m => 
    m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.location?.toLowerCase().includes(searchQuery.toLowerCase())
  ).filter(m => !friends.some(f => f.auth_id === m.auth_id))

  useEffect(() => {
    if (!authLoading && user) {
      fetchData()
    }
  }, [authLoading, user, fetchData])

  const handleAcceptRequest = async (requestId: string) => {
    if (!supabase) return
    setIsActionLoading(requestId)
    triggerHaptic('impact')
    try {
      const { error } = await supabase
        .from("friends")
        .update({ status: "accepted" })
        .eq("id", requestId)
      
      if (error) throw error
      toast.success("Friend request accepted!")
      fetchData()
    } catch (e) {
      toast.error("Failed to accept request")
    } finally {
      setIsActionLoading(null)
    }
  }

  const handleDeclineRequest = async (requestId: string) => {
    if (!supabase) return
    setIsActionLoading(requestId)
    triggerHaptic('impact')
    try {
      const { error } = await supabase
        .from("friends")
        .delete()
        .eq("id", requestId)
      
      if (error) throw error
      toast.success("Request declined")
      fetchData()
    } catch (e) {
      toast.error("Failed to decline request")
    } finally {
      setIsActionLoading(null)
    }
  }

  const handleSendRequest = async (targetAuthId: string) => {
    if (!user || !supabase) return
    setIsActionLoading(targetAuthId)
    triggerHaptic('impact')
    try {
      const { error } = await supabase
        .from("friends")
        .insert({
          user_id: user.id,
          friend_id: targetAuthId,
          status: "pending"
        })
      
      if (error) throw error
      toast.success("Friend request sent!")
      fetchData()
    } catch (e) {
      toast.error("Failed to send request")
    } finally {
      setIsActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Users className="w-12 h-12 text-blue-500 animate-pulse mb-4" />
        <p className="text-slate-500 font-medium">Loading friends & surfers...</p>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-20">
      {/* 1. Your Friends Drawer */}
      <Drawer open={showFriendsList} onOpenChange={setShowFriendsList}>
        <DrawerTrigger asChild>
          <button 
            onClick={() => triggerHaptic('selection')}
            className="w-full flex items-center justify-between p-5 bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm active:scale-[0.98] transition-all border-none"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-blue-500" />
              </div>
              <div className="text-left">
                <h3 className="font-black text-slate-900 dark:text-white uppercase tracking-tight">Your Friends</h3>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{friends.length} Connected</p>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-xl">
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </button>
        </DrawerTrigger>
        <DrawerContent className="h-[85vh] bg-white dark:bg-slate-900 border-none rounded-t-[3rem]">
          <div className="mx-auto w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full mt-3 mb-2" />
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-center font-black uppercase tracking-[0.2em] text-slate-900 dark:text-white pt-2">Your Friends</DrawerTitle>
            <DrawerDescription className="sr-only">Manage your friends and connections</DrawerDescription>
          </DrawerHeader>
          
          <ScrollArea className="flex-1 px-6 pb-12">
            <div className="space-y-6 pt-4">
              {/* Pending Requests */}
              {pendingRequests.length > 0 && (
                <div className="space-y-3">
                  <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-500 px-1">Friend Requests</h2>
                  {pendingRequests.map(req => (
                    <div key={req.id} className="bg-blue-50/50 dark:bg-blue-900/10 rounded-3xl p-4 flex items-center justify-between border border-blue-100 dark:border-blue-900/30">
                      <Link href={`/user/${req.sender.slug || req.sender.auth_id}`} className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 border border-slate-100 dark:border-slate-800">
                          <AvatarImage src={req.sender.avatar_url || ""} />
                          <AvatarFallback className="bg-slate-100 text-slate-500 font-bold">
                            {req.sender.first_name?.charAt(0) || req.sender.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="text-sm font-bold text-slate-900 dark:text-white">{req.sender.first_name} {req.sender.last_name}</h4>
                          <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Wants to connect</p>
                        </div>
                      </Link>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="h-9 w-9 rounded-xl bg-blue-500 hover:bg-blue-600 text-white p-0"
                          onClick={() => handleAcceptRequest(req.id)}
                          disabled={isActionLoading === req.id}
                        >
                          <Check className="w-5 h-5" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-9 w-9 rounded-xl border-slate-200 text-slate-400 p-0"
                          onClick={() => handleDeclineRequest(req.id)}
                          disabled={isActionLoading === req.id}
                        >
                          <X className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Friends List */}
              <div className="space-y-3 pb-8">
                <div className="flex items-center justify-between px-1">
                  <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">My Friends</h2>
                  <Badge variant="secondary" className="bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400 text-[8px] font-black uppercase px-2 rounded-lg">{friends.length}</Badge>
                </div>
                {friends.length === 0 ? (
                  <div className="text-center py-12 space-y-3">
                    <Users className="w-12 h-12 text-slate-200 mx-auto" />
                    <p className="text-slate-400 italic text-sm font-medium">No friends yet. Discover some below!</p>
                  </div>
                ) : (
                  friends.map(friend => (
                    <div key={friend.id} className="bg-slate-50 dark:bg-slate-800/50 rounded-3xl p-4 flex items-center justify-between group active:scale-[0.98] transition-all border border-transparent shadow-sm">
                      <Link href={`/user/${friend.slug || friend.auth_id}`} className="flex items-center gap-4 flex-1">
                        <Avatar className="h-12 w-12 border-2 border-white dark:border-slate-800 shadow-sm">
                          <AvatarImage src={friend.avatar_url || ""} />
                          <AvatarFallback className="bg-blue-100 text-blue-600 font-black">
                            {friend.first_name?.charAt(0) || friend.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="font-bold text-slate-900 dark:text-white">
                            {friend.first_name} {friend.last_name}
                          </h4>
                          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{friend.location || "Global Nomad"}</p>
                        </div>
                      </Link>
                      <Link href={`/messages?recipientId=${friend.auth_id}`} onClick={() => triggerHaptic('impact')}>
                        <Button variant="ghost" size="icon" className="rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-500 h-10 w-10">
                          <MessageCircle className="w-5 h-5 fill-current" />
                        </Button>
                      </Link>
                    </div>
                  ))
                )}
              </div>
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>

      {/* 2. Discover Surfers Drawer */}
      <Drawer open={showDiscoverList} onOpenChange={setShowDiscoverList}>
        <DrawerTrigger asChild>
          <button 
            onClick={() => triggerHaptic('selection')}
            className="w-full flex items-center justify-between p-5 bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm active:scale-[0.98] transition-all border-none"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-500" />
              </div>
              <div className="text-left">
                <h3 className="font-black text-slate-900 dark:text-white uppercase tracking-tight">Discover Surfers</h3>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Connect with community</p>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-xl">
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </button>
        </DrawerTrigger>
        <DrawerContent className="h-[90vh] bg-white dark:bg-slate-900 border-none rounded-t-[3rem]">
          <div className="mx-auto w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full mt-3 mb-2" />
          <DrawerHeader className="pb-0">
            <DrawerTitle className="text-center font-black uppercase tracking-[0.2em] text-slate-900 dark:text-white pt-2">Discover Surfers</DrawerTitle>
            <DrawerDescription className="sr-only">Find and connect with other surfers in the community</DrawerDescription>
          </DrawerHeader>

          <div className="px-6 pt-4 pb-2">
            <div className="relative w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input 
                placeholder="Search surfers by name or location..." 
                className="pl-12 h-14 rounded-2xl border-none bg-slate-50 dark:bg-slate-800 shadow-inner text-lg font-medium"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <ScrollArea className="flex-1 px-6 pb-12">
            <div className="grid grid-cols-1 gap-3 pt-2 pb-12">
              {filteredMembers.length === 0 ? (
                <div className="text-center py-12 space-y-3">
                  <Search className="w-12 h-12 text-slate-200 mx-auto" />
                  <p className="text-slate-400 italic">No surfers found matching your search.</p>
                </div>
              ) : (
                filteredMembers.map(member => (
                  <div key={member.id} className="bg-slate-50/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-[2rem] p-4 flex items-center justify-between group active:scale-[0.98] transition-all border border-transparent shadow-sm">
                    <Link href={`/user/${member.slug || member.auth_id}`} className="flex items-center gap-4 flex-1">
                      <Avatar className="h-14 w-14 border-2 border-white dark:border-slate-800 shadow-sm">
                        <AvatarImage src={member.avatar_url || ""} />
                        <AvatarFallback className="bg-slate-100 text-slate-400 font-black">
                          {member.first_name?.charAt(0) || member.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <h4 className="font-black text-slate-900 dark:text-white truncate">
                          {member.first_name || member.name} {member.last_name}
                        </h4>
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <MapPin className="w-3 h-3 text-blue-500" />
                          <span className="text-[10px] font-bold uppercase tracking-wider truncate">{member.location || "Ocean Bound"}</span>
                        </div>
                        <Badge variant="secondary" className="mt-2 bg-white dark:bg-slate-700 text-[8px] font-black uppercase rounded-lg border-none text-slate-400">
                          {member.surf_experience || "Beginner"}
                        </Badge>
                      </div>
                    </Link>
                    {sentRequests.includes(member.auth_id) ? (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-12 w-12 rounded-[1.25rem] bg-slate-50 dark:bg-slate-800 text-slate-400 cursor-default"
                      >
                        <Clock className="w-6 h-6" />
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-12 w-12 rounded-[1.25rem] border-blue-100 dark:border-blue-900/30 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all shadow-sm"
                        onClick={() => handleSendRequest(member.auth_id)}
                        disabled={isActionLoading === member.auth_id}
                      >
                        <UserPlus className="w-6 h-6" />
                      </Button>
                    )}
                  </div>
                )
              ))}
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>

      {/* Activity from Friends Section (Now Always Visible) */}
      <section className="space-y-6 pt-2">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Activity from Friends</h2>
          <Badge variant="secondary" className="bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 text-[8px] font-black uppercase px-2 rounded-lg">Live updates</Badge>
        </div>
        
        <div className="flex flex-col gap-6">
          {/* Upcoming Sessions Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center">
                  <Waves className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Upcoming Sessions</h2>
                  <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Friends' surf plans</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-blue-500 hover:bg-blue-50 px-3 border border-slate-100 dark:border-slate-800 shadow-sm"
                onClick={() => {
                  triggerHaptic('selection');
                  setShowSessionsHistory(true);
                }}
              >
                History
              </Button>
            </div>
            
            {sessions.length === 0 ? (
              <Card className="bg-white dark:bg-slate-900 border-dashed border-2 border-slate-100 dark:border-slate-800/50 rounded-[2rem] overflow-hidden">
                <CardContent className="p-10 text-center flex flex-col items-center">
                  <Waves className="w-10 h-10 text-slate-200 dark:text-slate-800 mb-4 opacity-50" />
                  <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-1">No sessions planned</h4>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 max-w-[180px]">
                    Your friends haven't shared any upcoming surf plans yet.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {sessions.map(session => (
                  <Link key={`session-${session.id}`} href={`/spot/${session.surf_spots.id}`} className="group active:scale-[0.98] transition-all">
                    <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-slate-100 dark:border-slate-800 rounded-[2rem] p-5 shadow-sm relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-8 opacity-5">
                        <Waves className="w-24 h-24 rotate-12 text-blue-500" />
                      </div>
                      <div className="relative z-10 space-y-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 border-2 border-white dark:border-slate-800 shadow-sm">
                            <AvatarImage src={session.users.avatar_url || ""} />
                          <AvatarFallback className="bg-blue-50 text-blue-500 font-bold">
                            {(session.users.first_name || session.users.name || 'S').charAt(0)}
                          </AvatarFallback>
                          </Avatar>
                          <div>
                            <h4 className="font-black text-slate-900 dark:text-white truncate">
                              {session.users.first_name || session.users.name || session.users.email?.split('@')[0] || 'Surfer'} @ {session.surf_spots.name}
                            </h4>
                            <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">{session.surf_spots.country || "Surf Spot"}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-slate-500 bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-3 border border-slate-100 dark:border-slate-800">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-blue-500" />
                            <span>{format(new Date(session.planned_date), "MMM d")}</span>
                          </div>
                          {session.planned_time && (
                            <div className="flex items-center gap-1.5 border-l border-slate-200 dark:border-slate-700 pl-4">
                              <span className="text-slate-300">|</span>
                              <Clock className="w-3.5 h-3.5 text-blue-500 ml-2" />
                              <span>{session.planned_time}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Parties Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Upcoming Parties</h2>
                  <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Shared sessions</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-blue-500 hover:bg-blue-50 px-3 border border-slate-100 dark:border-slate-800 shadow-sm"
                onClick={() => {
                  triggerHaptic('selection');
                  setShowPartiesHistory(true);
                }}
              >
                History
              </Button>
            </div>
            
            {friendParties.length === 0 ? (
              <Card className="bg-white dark:bg-slate-900 border-dashed border-2 border-slate-100 dark:border-slate-800/50 rounded-[2rem] overflow-hidden">
                <CardContent className="p-10 text-center flex flex-col items-center">
                  <Users className="w-10 h-10 text-slate-200 dark:text-slate-800 mb-4 opacity-50" />
                  <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-1">No parties found</h4>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 max-w-[180px]">
                    Your friends haven't organized any surf parties lately.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {friendParties.map(party => (
                  <Link key={`party-${party.id}`} href={`/explore?tab=parties`} className="group active:scale-[0.98] transition-all">
                    <div className="bg-blue-500 text-white rounded-[2rem] p-5 shadow-lg shadow-blue-500/20 relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-8 opacity-10">
                        <Users className="w-24 h-24 rotate-12" />
                      </div>
                      <div className="relative z-10 space-y-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 border-2 border-white/30">
                            <AvatarImage src={party.organizer.avatar_url || ""} />
                            <AvatarFallback className="bg-white/20 text-white font-bold">
                            {(party.organizer.name || 'F').charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <h4 className="font-black text-lg truncate">{party.title}</h4>
                          <p className="text-[10px] font-black uppercase tracking-wider opacity-80">Organized by {party.organizer.name ? party.organizer.name.split(' ')[0] : 'a friend'}</p>
                        </div>
                        </div>
                        <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest bg-black/10 rounded-2xl p-3 backdrop-blur-sm border border-white/10">
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5" />
                            <span className="truncate">{party.surf_spots.name}</span>
                          </div>
                          <div className="flex items-center gap-1.5 border-l border-white/20 pl-4">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{format(new Date(party.party_date), "MMM d")}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      {/* 3. Session History Drawer */}
      <Drawer open={showSessionsHistory} onOpenChange={setShowSessionsHistory}>
        <DrawerContent className="h-[85vh] bg-white dark:bg-slate-900 border-none rounded-t-[3rem]">
          <div className="mx-auto w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full mt-3 mb-2" />
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-center font-black uppercase tracking-[0.2em] text-slate-900 dark:text-white pt-2">Friends' Session History</DrawerTitle>
            <DrawerDescription className="sr-only">Historical surf sessions from your friends</DrawerDescription>
          </DrawerHeader>
          
          <ScrollArea className="flex-1 px-6 pb-12">
            {loadingHistory ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Waves className="w-12 h-12 text-blue-500 animate-pulse mb-4" />
                <p className="text-slate-500 font-medium italic">Retrieving past sessions...</p>
              </div>
            ) : historySessions.length === 0 ? (
              <div className="text-center py-20">
                <Waves className="w-16 h-16 text-slate-200 dark:text-slate-800 mx-auto mb-6" />
                <p className="text-slate-400 italic font-medium">No historical sessions found from your friends.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3 py-4">
                {historySessions.map((session) => (
                  <Link key={`hist-sess-${session.id}`} href={`/spot/${session.surf_spots.id}`} className="group active:scale-[0.98] transition-all">
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-3xl p-4 flex items-center gap-4 border border-transparent shadow-sm hover:border-blue-100 dark:hover:border-blue-900/30">
                      <Avatar className="h-12 w-12 border-2 border-white dark:border-slate-800 shadow-sm shrink-0">
                        <AvatarImage src={session.users.avatar_url || ""} />
                        <AvatarFallback className="bg-blue-100 text-blue-600 font-black text-sm">
                          {(session.users.first_name || session.users.name || 'S').charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-black text-sm text-slate-900 dark:text-slate-100 truncate">
                            {session.users.first_name || session.users.name || session.users.email?.split('@')[0] || 'Surfer'}
                          </span>
                          <div className="flex items-center gap-1 text-slate-400 shrink-0">
                            <Calendar className="w-3 h-3" />
                            <span className="text-[10px] font-black uppercase tracking-wider">
                              {format(new Date(session.session_date), "MMM d")}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3 h-3 text-blue-500" />
                          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 truncate">
                            {session.surf_spots.name}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </ScrollArea>
        </DrawerContent>
      </Drawer>

      {/* 4. Party History Drawer */}
      <Drawer open={showPartiesHistory} onOpenChange={setShowPartiesHistory}>
        <DrawerContent className="h-[85vh] bg-white dark:bg-slate-900 border-none rounded-t-[3rem]">
          <div className="mx-auto w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full mt-3 mb-2" />
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-center font-black uppercase tracking-[0.2em] text-slate-900 dark:text-white pt-2">Friends' Party History</DrawerTitle>
            <DrawerDescription className="sr-only">Historical surf parties from your friends</DrawerDescription>
          </DrawerHeader>
          
          <ScrollArea className="flex-1 px-6 pb-12">
            {loadingHistory ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Users className="w-12 h-12 text-blue-500 animate-pulse mb-4" />
                <p className="text-slate-500 font-medium italic">Retrieving past parties...</p>
              </div>
            ) : historyParties.length === 0 ? (
              <div className="text-center py-20">
                <Users className="w-16 h-16 text-slate-200 dark:text-slate-800 mx-auto mb-6" />
                <p className="text-slate-400 italic font-medium">No historical parties found from your friends.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3 py-4">
                {historyParties.map((party) => (
                  <Link key={`hist-party-${party.id}`} href="/explore?tab=parties" className="group active:scale-[0.98] transition-all">
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-3xl p-4 flex items-center gap-4 border border-transparent shadow-sm hover:border-teal-100 dark:hover:border-teal-900/30">
                      <Avatar className="h-12 w-12 border-2 border-white dark:border-slate-800 shadow-sm shrink-0">
                        <AvatarImage src={party.organizer.avatar_url || ""} />
                        <AvatarFallback className="bg-teal-100 text-teal-600 font-black text-sm">
                          {(party.organizer.first_name || party.organizer.name || 'F').charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-black text-sm text-slate-900 dark:text-slate-100 truncate group-hover:text-teal-500 transition-colors">
                          {party.title}
                        </h4>
                        <div className="flex items-center gap-3 mt-1">
                          <div className="flex items-center gap-1 text-[10px] font-black uppercase text-slate-400">
                            by {party.organizer.first_name || party.organizer.name || 'a friend'}
                          </div>
                          <div className="flex items-center gap-1 text-slate-400">
                            <Calendar className="w-3 h-3" />
                            <span className="text-[10px] font-black uppercase">
                              {format(new Date(party.party_date), "MMM d")}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-slate-400">
                            <MapPin className="w-3 h-3" />
                            <span className="text-xs font-bold truncate max-w-[100px]">{party.surf_spots.name}</span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-teal-500 transition-colors shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
